import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    IconButton,
    CircularProgress,
    Alert,
    Tooltip,
    ThemeProvider,
    createTheme,

} from '@mui/material';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InventoryIcon from '@mui/icons-material/Inventory';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { useUser } from '../../../contexts/UserContext';
import { loginRequest } from '../../../authConfig';
import styles from '../../../styles/purchaseInvoices/invoiceManagement/GoodsReceiptCreationScreenStyles';

const cacheRtl = createCache({
    key: 'muirtl',
    stylisPlugins: [prefixer, rtlPlugin],
});

const rtlTheme = createTheme({
    direction: 'rtl',
});


const GoodsReceiptCreationScreen = ({
    orderData,
    receiptDate,
    invoice,
    onBack,
    onSuccess
}) => {
    const { instance, accounts } = useMsal();
    const { user } = useUser();
    
    const [items, setItems] = useState([]);
    const [receivingWarehouse, setReceivingWarehouse] = useState(''); // מחסן מקבל
    const [deletedItems, setDeletedItems] = useState(new Set());
    const [isCreating, setIsCreating] = useState(false);
    const [isFinalizing, setIsFinalizing] = useState(false);
    const [error, setError] = useState('');
    const [createdDocNo, setCreatedDocNo] = useState(null);

    useEffect(() => {
        if (orderData?.items) {
            setItems(orderData.items.map((item, index) => ({
                ...item,
                id: index,
                quantity: item.quantity || 0,
                price: item.unitPrice || 0,
            })));
        }
        if (orderData?.warehouse) {
            setReceivingWarehouse(orderData.warehouse);
        }
    }, [orderData]);

    const activeItems = useMemo(() => {
        return items.filter((_, index) => !deletedItems.has(index));
    }, [items, deletedItems]);

    const total = useMemo(() => {
        return activeItems.reduce((sum, item) => {
            return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0);
        }, 0);
    }, [activeItems]);

    const handleItemChange = (index, field, value) => {
        setItems(prevItems => {
            const newItems = [...prevItems];
            newItems[index] = {
                ...newItems[index],
                [field]: value
            };
            return newItems;
        });
    };

    const handleDeleteItem = (index) => {
        setDeletedItems(prev => new Set([...prev, index]));
    };

    const handleRestoreItem = (index) => {
        setDeletedItems(prev => {
            const newSet = new Set(prev);
            newSet.delete(index);
            return newSet;
        });
    };

    const handleCreateGoodsReceipt = async () => {
        if (activeItems.length === 0) {
            setError('יש לבחור לפחות שורה אחת');
            return;
        }

        setError('');
        setIsCreating(true);

        try {
            const tokenResponse = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
            });

            const payload = {
                orderName: orderData.orderName,
                receiptDate: receiptDate,
                supplierNumber: orderData.supplierNumber,
                branchName: orderData.branchName,
                receivingWarehouse: receivingWarehouse, // מחסן מקבל
                booknum: invoice?.booknum || '', // מספר תעודת ספק
                finalize: true, // ליצור כסופית
                items: activeItems.map(item => ({
                    orderName: orderData.orderName,
                    itemCode: item.itemCode,
                    kline: item.kline, // KLINE from original order
                    quantity: parseFloat(item.quantity) || 0,
                    price: parseFloat(item.price) || 0,
                    budgetCode: item.budgetCode || '',
                    exchange: item.exchangeRate || 1,
                    lineNumber: item.kline || item.lineNumber
                })),
                invoiceId: invoice?.id
            };

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/goods-receipt/create`,
                payload,
                {
                    headers: { Authorization: `Bearer ${tokenResponse.accessToken}` }
                }
            );

            const result = response.data.data;
            setCreatedDocNo(result?.docNo);

            if (result?.finalized || result?.status === 'סופית') {
                if (onSuccess) {
                    setTimeout(() => {
                        onSuccess(result.docNo);
                    }, 1500);
                }
            } else if (result?.finalizeError) {
                setError(`קבלת סחורה נוצרה כטיוטא. שגיאה בסיפור: ${result.finalizeError}`);
            }
        } catch (err) {
            console.error('Error creating goods receipt:', err);
            setError(err.response?.data?.message || err.message || 'שגיאה ביצירת קבלת סחורה');
        } finally {
            setIsCreating(false);
        }
    };

    const handleFinalizeGoodsReceipt = async () => {
        if (!createdDocNo) {
            setError('יש ליצור קבלת סחורה קודם');
            return;
        }

        setError('');
        setIsFinalizing(true);

        try {
            const tokenResponse = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
            });

            await axios.post(
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/goods-receipt/${createdDocNo}/finalize`,
                {},
                {
                    headers: { Authorization: `Bearer ${tokenResponse.accessToken}` }
                }
            );

            if (onSuccess) {
                setTimeout(() => {
                    onSuccess(createdDocNo);
                }, 1500);
            }
        } catch (err) {
            console.error('Error finalizing goods receipt:', err);
            setError(err.response?.data?.message || err.message || 'שגיאה בסיפור קבלת סחורה');
        } finally {
            setIsFinalizing(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('he-IL', {
            style: 'currency',
            currency: 'ILS'
        }).format(value);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('he-IL');
    };

    return (
        <CacheProvider value={cacheRtl}>
            <ThemeProvider theme={rtlTheme}>
                <Box sx={styles.container} dir="rtl">
                    <Box sx={styles.header}>
                        <Box sx={styles.headerLeft}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowForwardIcon />}
                                onClick={onBack}
                                sx={styles.backButton}
                                disabled={isCreating || isFinalizing}
                            >
                                חזרה
                            </Button>
                            <Box sx={styles.titleBox}>
                                <Box sx={styles.titleIcon}>
                                    <InventoryIcon />
                                </Box>
                                <Box>
                                    <Typography sx={styles.title}>
                                        יצירת קבלת סחורה
                                    </Typography>
                                    <Typography sx={styles.subtitle}>
                                        עבור הזמנה <Box component="span" sx={styles.orderBadge}>{orderData?.orderName}</Box>
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {createdDocNo && (
                        <Box sx={styles.createdDocInfo}>
                            <CheckCircleIcon sx={{ color: '#22c55e', fontSize: '1.5rem' }} />
                            <Typography sx={styles.createdDocText}>
                                קבלת סחורה נוצרה ואושרה בהצלחה כסופית - מספר מסמך: <strong>{createdDocNo}</strong>
                            </Typography>
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" sx={styles.errorAlert} onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}

                    <Paper sx={styles.infoCard}>
                        <Box sx={styles.infoGrid}>
                            <Box sx={styles.infoItem}>
                                <Typography sx={styles.infoLabel}>תאריך</Typography>
                                <Typography sx={styles.infoValue}>{formatDate(receiptDate)}</Typography>
                            </Box>
                            <Box sx={styles.infoItem}>
                                <Typography sx={styles.infoLabel}>מספר הזמנה</Typography>
                                <Typography sx={styles.infoValue}>{orderData?.orderName}</Typography>
                            </Box>
                            <Box sx={styles.infoItem}>
                                <Typography sx={styles.infoLabel}>מס' ספק</Typography>
                                <Typography sx={styles.infoValue}>{orderData?.supplierNumber}</Typography>
                            </Box>
                            <Box sx={styles.infoItem}>
                                <Typography sx={styles.infoLabel}>שם ספק</Typography>
                                <Typography sx={styles.infoValue}>
                                    {orderData?.supplierName || orderData?.supplierNumber}
                                </Typography>
                            </Box>
                            <Box sx={styles.infoItem}>
                                <Typography sx={styles.infoLabel}>סניף</Typography>
                                <Typography sx={styles.infoValue}>
                                    {orderData?.branchName && orderData?.branchDescription 
                                        ? `${orderData.branchName} - ${orderData.branchDescription}`
                                        : orderData?.branchDescription || orderData?.branchName || '-'}
                                </Typography>
                            </Box>
                            <Box sx={styles.infoItem}>
                                <Typography sx={styles.infoLabel}>מחסן מקבל</Typography>
                                <TextField
                                    size="small"
                                    value={receivingWarehouse}
                                    onChange={(e) => setReceivingWarehouse(e.target.value)}
                                    disabled={!!createdDocNo}
                                    placeholder="הקלד קוד מחסן"
                                    sx={styles.warehouseInput}
                                />
                            </Box>
                            {invoice?.booknum && (
                                <Box sx={styles.infoItem}>
                                    <Typography sx={styles.infoLabel}>מספר חשבונית</Typography>
                                    <Typography sx={styles.infoValue}>{invoice.booknum}</Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>

                    <Paper sx={styles.tableCard}>
                        <Box sx={styles.tableHeader}>
                            <Typography sx={styles.tableTitle}>
                                <InventoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                פריטים
                            </Typography>
                            <Box sx={styles.tableHeaderRight}>
                                <Typography sx={styles.rowCount}>
                                    {activeItems.length} שורות פעילות
                                    {deletedItems.size > 0 && ` (${deletedItems.size} נמחקו)`}
                                </Typography>
                            </Box>
                        </Box>

                        <TableContainer sx={styles.tableContainer}>
                            <Table sx={styles.table} stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={styles.tableHeaderCell}>מק"ט</TableCell>
                                        <TableCell sx={styles.tableHeaderCell}>תיאור מוצר</TableCell>
                                        <TableCell sx={styles.tableHeaderCell}>כמות</TableCell>
                                        <TableCell sx={styles.tableHeaderCell}>מחיר ליחידה</TableCell>
                                        <TableCell sx={styles.tableHeaderCell}>סה"כ</TableCell>
                                        <TableCell sx={styles.tableHeaderCell} align="center">הסרת שורה</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6}>
                                                <Box sx={styles.emptyState}>
                                                    <InventoryIcon sx={styles.emptyIcon} />
                                                    <Typography sx={styles.emptyText}>
                                                        אין פריטים בהזמנה
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        items.map((item, index) => {
                                            const isDeleted = deletedItems.has(index);
                                            const rowTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0);

                                            return (
                                                <TableRow
                                                    key={index}
                                                    sx={styles.tableRow}
                                                    className={isDeleted ? 'deleted' : ''}
                                                >
                                                    <TableCell sx={styles.tableCell}>
                                                        {item.itemCode}
                                                    </TableCell>
                                                    <TableCell sx={styles.tableCell}>
                                                        {item.description}
                                                    </TableCell>
                                                    <TableCell sx={{ ...styles.tableCell, ...styles.editableCell }}>
                                                        <TextField
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                            size="small"
                                                            sx={styles.editInput}
                                                            disabled={isDeleted || !!createdDocNo}
                                                            inputProps={{ min: 0, step: 0.01 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ ...styles.tableCell, ...styles.editableCell }}>
                                                        <TextField
                                                            type="number"
                                                            value={item.price}
                                                            onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                                                            size="small"
                                                            sx={styles.editInput}
                                                            disabled={isDeleted || !!createdDocNo}
                                                            inputProps={{ min: 0, step: 0.01 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={styles.tableCell}>
                                                        {formatCurrency(rowTotal)}
                                                    </TableCell>
                                                    <TableCell sx={styles.tableCell} align="center">
                                                        {!createdDocNo && (
                                                            isDeleted ? (
                                                                <Tooltip title="שחזר שורה">
                                                                    <IconButton
                                                                        onClick={() => handleRestoreItem(index)}
                                                                        sx={styles.restoreRowButton}
                                                                        size="small"
                                                                    >
                                                                        <RestoreIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            ) : (
                                                                <Tooltip title="הסר שורה">
                                                                    <IconButton
                                                                        onClick={() => handleDeleteItem(index)}
                                                                        sx={styles.deleteRowButton}
                                                                        size="small"
                                                                    >
                                                                        <DeleteOutlineIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    <Paper sx={styles.actionsContainer}>
                        <Box sx={styles.summaryBox}>
                            <Box sx={styles.summaryItem}>
                                <Typography sx={styles.summaryLabel}>סה"כ שורות:</Typography>
                                <Typography sx={styles.summaryValue}>{activeItems.length}</Typography>
                            </Box>
                            <Box sx={styles.summaryItem}>
                                <Typography sx={styles.summaryLabel}>סה"כ סכום:</Typography>
                                <Typography sx={styles.summaryValue}>{formatCurrency(total)}</Typography>
                            </Box>
                        </Box>

                        <Box sx={styles.actionsButtons}>
                            <Button
                                variant="outlined"
                                onClick={onBack}
                                sx={styles.cancelButton}
                                disabled={isCreating}
                            >
                                ביטול
                            </Button>
                            
                            <Button
                                variant="contained"
                                onClick={handleCreateGoodsReceipt}
                                sx={styles.saveDraftButton}
                                disabled={isCreating || activeItems.length === 0}
                                startIcon={isCreating ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                            >
                                {isCreating ? 'יוצר קבלת סחורה...' : 'צור קבלת סחורה'}
                            </Button>
                        </Box>
                    </Paper>

                    {isCreating && (
                        <Box sx={styles.loadingOverlay}>
                            <Box sx={styles.loadingContent}>
                                <CircularProgress />
                                <Typography sx={styles.loadingText}>
                                    יוצר קבלת סחורה ומאשר בפריוריטי...
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            </ThemeProvider>
        </CacheProvider>
    );
};

export default GoodsReceiptCreationScreen;
